import { useState, useEffect, useMemo, useRef } from 'react';
import type { Client, Assessment, PostureAnalysis } from '@/lib/types';
import type { ClientNeedsTag } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MoreHorizontal, PlusCircle, Pencil, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  subscribeToClientAssessments,
  deleteAssessment as deleteAssessmentApi,
} from '@/components/sop-intake/services/assessments-api';
import {
  subscribeToClientPostureAnalyses,
  deletePostureAnalysis as deletePostureAnalysisApi,
} from '@/components/posture-analysis/services/posture-analyses-api';
import {
  subscribeToPractitionerNotes,
  createPractitionerNote,
  deletePractitionerNote,
} from '@/components/clients/services/practitioner-notes-api';
import logger from '@/lib/logging';
import { useClient } from '@/context/client-context';
import { exportPostureAnalysisToPdf, exportSopAnalysisToPdf } from '@/lib/pdf-export';
import { useToast } from '@/hooks/use-toast';
import { formatDateLocale, safeToDate } from '@/lib/utils';
import { useSettings } from '@/components/settings/lib/settings-context';
import { useIsMobile } from '@/hooks/use-mobile';
import RemoteCaptureBadge from '@/components/remote-assessment/RemoteCaptureBadge';
import { isRemotePostureAnalysis, isRemoteSopAssessment } from '@/lib/remote-capture';

const CAPTURED_VIEW_ORDER = ['Anterior', 'Posterior', 'Left', 'Right'] as const;

function formatCapturedViewNames(captured: PostureAnalysis['captured'] | undefined): string {
  if (!captured) return 'N/A';
  const names = CAPTURED_VIEW_ORDER.filter((view) => Boolean(captured[view]?.imageData));
  return names.length > 0 ? names.join(', ') : 'N/A';
}
import { listDrillPlanAssignments } from '@/components/drill-planner/services/drill-plans-api';
import type { DrillPlanAssignment } from '@/lib/drill-planner/drill-plan-types';
import { buildClientActivityTimeline } from '@/lib/clients/activity-timeline';
import { MOBILE_BOTTOM_NAV_HEIGHT_PX } from '@/lib/mobile-immersive-chrome';
import type { ClientPractitionerNote } from '@/lib/clients/activity-timeline';
import { ClientFormSheet } from './ClientFormSheet';
import { ClientProfileHeader } from './profile/ClientProfileHeader';
import { ClientOverviewTab } from './profile/ClientOverviewTab';
import { ClientMedicalSummary } from './profile/ClientMedicalSummary';
import { ClientNeedsEditor } from './profile/ClientNeedsEditor';
import { buildClientPlannerFilters } from '@/lib/clients/needs-tags';
import { ClientActivityTimeline } from './profile/ClientActivityTimeline';
import { ClientProgramsTab } from './profile/ClientProgramsTab';
import { ClientNotesTab } from './profile/ClientNotesTab';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const experienceLevels = ['Beginner Lifter', 'Novice Lifter', 'Intermediate Lifter', 'Athlete'];
const painLevels = ['Minimal Pain', 'Some Pain', 'Lots of Pain'];

const PROFILE_TABS = [
  'overview',
  'timeline',
  'posture',
  'assessments',
  'programs',
  'notes',
] as const;

type ProfileTab = (typeof PROFILE_TABS)[number];

function isProfileTab(value: string | null): value is ProfileTab {
  return PROFILE_TABS.includes(value as ProfileTab);
}

export default function ClientPageContent({ client }: { client: Client }) {
  const { setClient } = useClient();
  const { settings } = useSettings();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [postureAnalyses, setPostureAnalyses] = useState<PostureAnalysis[]>([]);
  const [loadingPostureAnalyses, setLoadingPostureAnalyses] = useState(true);
  const [notes, setNotes] = useState<ClientPractitionerNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<DrillPlanAssignment[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [needsTags, setNeedsTags] = useState<ClientNeedsTag[]>(client.needsTags ?? []);
  const [editOpen, setEditOpen] = useState(false);

  const { toast } = useToast();
  const [deleteAssessmentId, setDeleteAssessmentId] = useState<string | null>(null);
  const [deletePostureAnalysisId, setDeletePostureAnalysisId] = useState<string | null>(null);

  const activeTab: ProfileTab = isProfileTab(searchParams.get('tab'))
    ? (searchParams.get('tab') as ProfileTab)
    : 'overview';

  const setActiveTab = (tab: ProfileTab) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  // Mobile tab row scrolls horizontally (overflow-x-auto) — keep the active trigger
  // in view when it changes (deep link, back/forward nav, or a tap near the edge)
  // instead of leaving it clipped off-screen. `inline: 'nearest'` only scrolls when
  // the trigger isn't already fully visible, so it never fights the swipe gesture.
  const tabTriggerRefs = useRef<Partial<Record<ProfileTab, HTMLButtonElement | null>>>({});
  useEffect(() => {
    tabTriggerRefs.current[activeTab]?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [activeTab]);

  useEffect(() => {
    if (client) setClient(client);
  }, [client, setClient]);

  useEffect(() => {
    setNeedsTags(client.needsTags ?? []);
  }, [client.id, client.needsTags]);

  const handleNeedsChange = (next: ClientNeedsTag[]) => {
    setNeedsTags(next);
    setClient({ ...client, needsTags: next });
  };

  const handleOpenLibraryForClient = () => {
    setClient({ ...client, needsTags });
    navigate('/drills', { state: { loadClientNeeds: true } });
  };

  const handleOpenPlannerForClient = () => {
    const clientWithNeeds = { ...client, needsTags };
    setClient(clientWithNeeds);
    navigate('/drill-planner?mode=create', {
      state: {
        filters: buildClientPlannerFilters(clientWithNeeds),
        clientId: client.id,
      },
    });
  };

  useEffect(() => {
    if (!user) {
      setLoadingAssessments(false);
      setLoadingPostureAnalyses(false);
      setLoadingNotes(false);
      return;
    }

    const unsubAssessments = subscribeToClientAssessments(
      client.id,
      (data) => {
        setAssessments(data);
        setLoadingAssessments(false);
      },
      undefined,
      (error) => {
        logger.error('Error fetching assessments: ', error);
        setLoadingAssessments(false);
      }
    );

    const unsubPosture = subscribeToClientPostureAnalyses(
      client.id,
      (data) => {
        setPostureAnalyses(data);
        setLoadingPostureAnalyses(false);
      },
      undefined,
      (error) => {
        logger.error('Error fetching posture analyses: ', error);
        setLoadingPostureAnalyses(false);
      }
    );

    const unsubNotes = subscribeToPractitionerNotes(
      client.id,
      (data) => {
        setNotes(data);
        setNotesError(null);
        setLoadingNotes(false);
      },
      undefined,
      (error) => {
        logger.error('Error fetching practitioner notes: ', error);
        setNotesError('Could not load notes. Check your connection or permissions and try again.');
        setLoadingNotes(false);
      }
    );

    return () => {
      unsubAssessments();
      unsubPosture();
      unsubNotes();
    };
  }, [client.id, user]);

  useEffect(() => {
    let cancelled = false;
    setLoadingAssignments(true);
    void listDrillPlanAssignments(client.id)
      .then((rows) => {
        if (!cancelled) setAssignments(rows);
      })
      .catch((err) => {
        logger.warn('Failed to load drill plan assignments', err);
        if (!cancelled) setAssignments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAssignments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client.id]);

  const timelineEvents = useMemo(
    () =>
      buildClientActivityTimeline({
        clientId: client.id,
        assessments,
        postureAnalyses,
        programAssignments: assignments,
        notes,
      }),
    [client.id, assessments, postureAnalyses, assignments, notes]
  );

  const profileImage = useMemo(() => {
    const sorted = [...postureAnalyses].sort((a, b) => {
      const ta = safeToDate(a.createdAt)?.getTime() ?? 0;
      const tb = safeToDate(b.createdAt)?.getTime() ?? 0;
      return tb - ta;
    });
    for (const analysis of sorted) {
      const img = analysis.captured?.Anterior?.imageData;
      if (img) return img;
    }
    return undefined;
  }, [postureAnalyses]);

  const handlePostureAnalysis = () => {
    setClient(client);
    navigate('/posture-analysis');
  };

  const handleSOPAssessment = () => {
    setClient(client);
    navigate('/sop-assessment');
  };

  const handleRemoteAssessment = () => {
    setClient(client);
    navigate('/remote-assessment');
  };
  const remoteAssessmentAvailable = settings.experimentalFeaturesEnabled === true && !isMobile;

  const deleteAssessment = async () => {
    if (!deleteAssessmentId || !user) return;
    try {
      await deleteAssessmentApi(client.id, deleteAssessmentId);
      toast({ title: 'Success', description: 'SOP assessment deleted successfully.' });
      setDeleteAssessmentId(null);
    } catch (error) {
      logger.error('Failed to delete SOP assessment:', error);
      toast({
        title: 'Delete Failed',
        description: 'Could not delete the SOP assessment.',
        variant: 'destructive',
      });
      setDeleteAssessmentId(null);
    }
  };

  const deletePostureAnalysis = async () => {
    if (!deletePostureAnalysisId || !user) return;
    try {
      await deletePostureAnalysisApi(client.id, deletePostureAnalysisId);
      toast({ title: 'Success', description: 'Posture analysis deleted successfully.' });
      setDeletePostureAnalysisId(null);
    } catch (error) {
      logger.error('Failed to delete posture analysis:', error);
      toast({
        title: 'Delete Failed',
        description: 'Could not delete the posture analysis.',
        variant: 'destructive',
      });
      setDeletePostureAnalysisId(null);
    }
  };

  const handleExportSopPdf = async (assessment: Assessment) => {
    try {
      await exportSopAnalysisToPdf(assessment, client);
      toast({ title: 'Success', description: 'SOP assessment exported as PDF.' });
    } catch (error) {
      logger.error('Failed to export SOP assessment:', error);
      toast({
        title: 'Export Failed',
        description: 'Could not export the SOP assessment.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateNote = async (text: string) => {
    if (!user?.uid) {
      toast({ title: 'Error', description: 'You must be signed in to save notes.', variant: 'destructive' });
      return;
    }
    try {
      await createPractitionerNote(client.id, text);
      toast({ title: 'Note saved' });
    } catch (error) {
      logger.error('Failed to save practitioner note: ', error);
      toast({
        title: 'Could not save note',
        description: 'Your note was not saved. Check your connection or permissions and try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!user) return;
    try {
      await deletePractitionerNote(client.id, noteId);
      toast({ title: 'Note deleted' });
    } catch (error) {
      logger.error('Failed to delete practitioner note: ', error);
      toast({
        title: 'Could not delete note',
        description: 'The note was not deleted. Check your connection or permissions and try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const timelineLoading = loadingAssessments || loadingPostureAnalyses || loadingAssignments || loadingNotes;

  return (
    <>
      <div className="flex flex-col gap-4 sm:gap-6 pb-24 lg:pb-0" data-testid="client-360-shell">
        <ClientProfileHeader
          client={client}
          profileImage={profileImage}
          onEdit={() => setEditOpen(true)}
          onPostureAnalysis={handlePostureAnalysis}
          onSopAssessment={handleSOPAssessment}
          onRemoteAssessment={remoteAssessmentAvailable ? handleRemoteAssessment : undefined}
        />

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ProfileTab)} className="w-full">
          <div className="mb-4 border-b sm:mb-6">
            <TabsList className="flex h-auto w-full flex-wrap justify-start rounded-none bg-transparent p-0">
              {(
                [
                  ['overview', 'Overview'],
                  ['timeline', 'Timeline'],
                  ['posture', 'Posture', postureAnalyses.length],
                  ['assessments', 'Assessments', assessments.length],
                  ['programs', 'Programs', assignments.length],
                  ['notes', 'Notes', notes.length],
                ] as const
              ).map(([value, label, count]) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  ref={(el) => {
                    tabTriggerRefs.current[value] = el;
                  }}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 sm:px-6 py-2.5"
                >
                  {label}
                  {count !== undefined && count > 0 && (
                    <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                      {count}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="overview">
            <div className="space-y-4 sm:space-y-6">
              <ClientOverviewTab
                client={client}
                assessmentCount={assessments.length}
                postureCount={postureAnalyses.length}
                programCount={assignments.length}
                noteCount={notes.length}
                postureAnalyses={postureAnalyses}
              />
              <ClientNeedsEditor
                client={client}
                needsTags={needsTags}
                postureAnalyses={postureAnalyses}
                assessments={assessments}
                onNeedsChange={handleNeedsChange}
                onOpenLibrary={handleOpenLibraryForClient}
                onOpenPlanner={handleOpenPlannerForClient}
              />
              <ClientMedicalSummary client={client} />
            </div>
          </TabsContent>

          <TabsContent value="timeline">
            <ClientActivityTimeline events={timelineEvents} loading={timelineLoading} />
          </TabsContent>

          <TabsContent value="programs">
            <ClientProgramsTab
              assignments={assignments}
              loading={loadingAssignments}
              clientId={client.id}
            />
          </TabsContent>

          <TabsContent value="notes">
            <ClientNotesTab
              notes={notes}
              loading={loadingNotes}
              error={notesError}
              currentUserUid={user?.uid}
              onCreate={handleCreateNote}
              onDelete={handleDeleteNote}
            />
          </TabsContent>

          <TabsContent value="assessments">
            <Card className="border-none shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>SOP assessments</CardTitle>
                <Button size="sm" onClick={handleSOPAssessment}>
                  <PlusCircle className="mr-2 h-4 w-4" /> New
                </Button>
              </CardHeader>
              <CardContent>
                {loadingAssessments ? (
                  <p className="text-sm text-muted-foreground">Loading assessments…</p>
                ) : assessments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No assessments yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & time</TableHead>
                        <TableHead className="hidden sm:table-cell">Experience</TableHead>
                        <TableHead className="hidden sm:table-cell">Pain</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assessments.map((assessment) => (
                        <TableRow
                          key={assessment.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (target.closest('button') || target.closest('[role="menu"]')) return;
                            navigate(`/clients/${client.id}/assessment/${assessment.id}`);
                          }}
                        >
                          <TableCell>
                            {formatDateLocale(assessment.createdAt, {
                              year: 'numeric',
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {isRemoteSopAssessment(assessment as unknown as Record<string, unknown>) && (
                              <RemoteCaptureBadge className="ml-2" />
                            )}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {experienceLevels[assessment.experienceLevel - 1] || 'N/A'}
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {painLevels[assessment.painLevel - 1] || 'N/A'}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/clients/${client.id}/assessment/${assessment.id}`}>
                                    View details
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/clients/${client.id}/assessment/${assessment.id}/edit`}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    Edit
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => handleExportSopPdf(assessment)}>
                                  Export PDF
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => setDeleteAssessmentId(assessment.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="posture">
            <Card className="border-none shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Posture analyses</CardTitle>
                <Button size="sm" onClick={handlePostureAnalysis}>
                  <PlusCircle className="mr-2 h-4 w-4" /> New
                </Button>
              </CardHeader>
              <CardContent>
                {loadingPostureAnalyses ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : postureAnalyses.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No posture analyses yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date & time</TableHead>
                        <TableHead>Views</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {postureAnalyses.map((analysis) => (
                        <TableRow key={analysis.id}>
                          <TableCell>
                            <Link
                              to={`/clients/${client.id}/posture-analysis/${analysis.id}`}
                              className="hover:underline"
                            >
                              {formatDateLocale(analysis.createdAt, {
                                year: 'numeric',
                                month: 'numeric',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Link>
                            {isRemotePostureAnalysis(analysis) && <RemoteCaptureBadge className="ml-2" />}
                          </TableCell>
                          <TableCell>
                            {formatCapturedViewNames(analysis.captured)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <span className="sr-only">Open menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <Link to={`/clients/${client.id}/posture-analysis/${analysis.id}`}>
                                    View analysis
                                  </Link>
                                </DropdownMenuItem>
                                {settings.experimentalFeaturesEnabled && (
                                  <DropdownMenuItem
                                    onSelect={() => exportPostureAnalysisToPdf(analysis, client)}
                                  >
                                    Export PDF
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onSelect={() => setDeletePostureAnalysisId(analysis.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sticky thumb-zone primary action on mobile — pinned above the bottom nav bar
          (BottomNav renders as a normal-flow sibling of `main`, not overlapping it, so
          this needs an explicit offset for its height + safe area rather than `bottom-0`). */}
      <div
        className="fixed inset-x-0 z-30 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:hidden"
        style={{ bottom: `calc(${MOBILE_BOTTOM_NAV_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))` }}
      >
        <Button type="button" className="h-12 w-full" onClick={handleSOPAssessment} data-testid="client-360-sticky-assessment-cta">
          <PlusCircle className="mr-2 h-4 w-4" />
          Assessment
        </Button>
      </div>

      <ClientFormSheet
        open={editOpen}
        onOpenChange={setEditOpen}
        client={client}
      />

      <AlertDialog open={!!deleteAssessmentId} onOpenChange={() => setDeleteAssessmentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the SOP assessment and all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteAssessment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletePostureAnalysisId} onOpenChange={() => setDeletePostureAnalysisId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the posture analysis and all associated images.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deletePostureAnalysis}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
